'use client'

import { localItemOptionType } from '@/app/ui/parts/default/sp/item/option'
import {
  AmazonPayCancelEvent,
  AmazonPayCartResult,
  AmazonPayCompleteCheckoutEvent,
  AmazonPayCompleteCheckoutResult,
  AmazonPayConfig,
  AmazonPayInitCheckoutEvent,
  amazonPayCompleteCheckout,
  amazonPayInitCheckout,
} from '@/lib/amazonpay/server'
import { createOptionFromCartItem } from '@/lib/cart/logic'
import {
  addAdditionalPurchaseOptionToCart,
  addCartWithOption,
  getCartItems,
} from '@/lib/cart/server'
import { serviceType } from '@/lib/service/server'
import { additionalPurchaseOptionElement } from '@/lib/service_type'
import { useCallback, useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    amazon?: {
      Pay: {
        renderJSButton: (
          selector: string,
          config: AmazonPayButtonConfig,
        ) => void
      }
    }
  }
}

interface AmazonPayRenderConfig {
  merchantId: string
  ledgerCurrency: string
  sandbox: boolean
  checkoutLanguage: string
  productType: string
  placement: string
  buttonColor: string
  checkoutSessionConfig: {
    storeId: string
    scopes: string[]
    paymentDetails: {
      paymentIntent: string
      canHandlePendingAuthorization: string
    }
    deliverySpecifications: {
      addressRestrictions: {
        type: string
        restrictions: Record<string, object>
      }
    }
  }
  onInitCheckout: (
    event: AmazonPayInitCheckoutEvent,
  ) => Promise<AmazonPayCartResult>
  onCompleteCheckout: (
    event: AmazonPayCompleteCheckoutEvent,
  ) => Promise<AmazonPayCompleteCheckoutResult | void>
  onCancel: (event: AmazonPayCancelEvent) => void
}

interface AmazonPayButtonConfig {
  merchantId: string
  ledgerCurrency: string
  sandbox: boolean
  checkoutLanguage: string
  productType: string
  placement: string
  buttonColor: string
  checkoutSessionConfig: {
    storeId: string
    scopes: string[]
    paymentDetails: {
      paymentIntent: string
      canHandlePendingAuthorization: string
    }
    deliverySpecifications?: {
      addressRestrictions: {
        type: string
        restrictions: Record<string, object>
      }
    }
  }
  onInitCheckout: (event: any) => Promise<any>
  onCompleteCheckout: (event: any) => Promise<any>
  onCancel: (event: any) => void
}

type AmazonPayButtonProps = {
  service: serviceType
  selectedOptions: localItemOptionType[]
  additionalOptions: additionalPurchaseOptionElement[]
  amazonPayConfig: AmazonPayConfig
}

export const AmazonPayButtonInItemPage: React.FC<AmazonPayButtonProps> = ({
  service,
  selectedOptions,
  amazonPayConfig,
  additionalOptions,
}) => {
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const isRenderedRef = useRef(false)
  const itemOptionsRef = useRef<localItemOptionType[]>(selectedOptions)
  const additionalOptionsRef =
    useRef<additionalPurchaseOptionElement[]>(additionalOptions)

  // ★ 決済処理中の状態管理
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingError, setProcessingError] = useState<string | null>(null)

  useEffect(() => {
    itemOptionsRef.current = selectedOptions
  }, [selectedOptions])
  useEffect(() => {
    additionalOptionsRef.current = additionalOptions
  }, [additionalOptions])

  const handleInitCheckout = useCallback(
    async (event: AmazonPayInitCheckoutEvent): Promise<AmazonPayCartResult> => {
      try {
        for (const option of itemOptionsRef.current) {
          const selectedOption =
            option.custom_option_id === 0
              ? createOptionFromCartItem(
                  option.name,
                  option.color ? option.color.name : '',
                  option.size ? option.size.name : '',
                )
              : undefined
          await addCartWithOption(
            service.service_id,
            option.item_id,
            option.custom_option_id,
            selectedOption,
          )
        }
        if (additionalOptionsRef.current.length > 0) {
          for (const additionalOption of additionalOptionsRef.current) {
            await addAdditionalPurchaseOptionToCart(
              service.service_id,
              additionalOption.item_id,
              additionalOption.name,
            )
          }
        }
        const cartItems = await getCartItems(service.service_id)
        const result = await amazonPayInitCheckout(
          service,
          cartItems,
          '',
          event.shippingAddress,
          event.buyer,
          event.billingAddress,
          event.scopes,
          event.checkoutLanguage,
        )
        return result
      } catch (err) {
        console.error('onInitCheckout error:', err)
        return { status: 'error', reasonCode: 'unknownError' }
      }
    },
    [service],
  )

  const handleCompleteCheckout = useCallback(
    async (
      event: AmazonPayCompleteCheckoutEvent,
    ): Promise<AmazonPayCompleteCheckoutResult | void> => {
      try {
        // ★ 処理開始
        setIsProcessing(true)
        setProcessingError(null)

        const result = await amazonPayCompleteCheckout(service, event)

        if ('success' in result && result.success && result.redirect_url) {
          // リダイレクトまでモーダルは表示したまま
          window.location.href = result.redirect_url
          return result
        }

        // エラーの場合
        if ('status' in result && result.status === 'error') {
          setProcessingError(
            'Amazon Payでのお支払いに失敗しました。別のお支払い方法をお試しください。',
          )
          // 3秒後にエラーを非表示
          setTimeout(() => {
            setIsProcessing(false)
            setProcessingError(null)
          }, 5000)
        }

        return result
      } catch (err) {
        console.error('onCompleteCheckout error:', err)
        setProcessingError(
          '決済処理中にエラーが発生しました。もう一度お試しください。',
        )
        setTimeout(() => {
          setIsProcessing(false)
          setProcessingError(null)
        }, 5000)
        return { status: 'error', reasonCode: 'unknownError' }
      }
    },
    [service],
  )

  const handleCancel = useCallback((_event: AmazonPayCancelEvent) => {
    console.log('Amazon Pay checkout cancelled', _event)
    setIsProcessing(false)
    setProcessingError(null)
  }, [])

  useEffect(() => {
    if (isRenderedRef.current) return

    const renderButton = () => {
      if (!window.amazon || !buttonContainerRef.current) return
      buttonContainerRef.current.innerHTML = ''

      const config: AmazonPayRenderConfig = {
        merchantId: amazonPayConfig.merchant_id,
        ledgerCurrency: 'JPY',
        sandbox: amazonPayConfig.sandbox,
        checkoutLanguage: 'ja_JP',
        productType: 'PayAndShip',
        placement: 'Checkout',
        buttonColor: 'Gold',
        checkoutSessionConfig: {
          storeId: amazonPayConfig.store_id,
          scopes: ['name', 'email', 'phoneNumber', 'billingAddress'],
          paymentDetails: {
            paymentIntent: 'AuthorizeWithCapture',
            canHandlePendingAuthorization: 'false',
          },
          deliverySpecifications: {
            addressRestrictions: {
              type: 'Allowed',
              restrictions: { JP: {} },
            },
          },
        },
        onInitCheckout: handleInitCheckout,
        onCompleteCheckout: handleCompleteCheckout,
        onCancel: handleCancel,
      }

      window.amazon.Pay.renderJSButton('#AmazonPayButton', config)
      isRenderedRef.current = true
    }

    if (window.amazon) {
      renderButton()
      return
    }

    const existingScript = document.querySelector(
      'script[src="https://static-fe.payments-amazon.com/checkout.js"]',
    )
    if (existingScript) {
      existingScript.addEventListener('load', renderButton)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://static-fe.payments-amazon.com/checkout.js'
    script.async = true
    script.onload = renderButton
    script.onerror = () => console.error('Failed to load Amazon Pay script')
    document.body.appendChild(script)

    return () => {
      isRenderedRef.current = false
    }
  }, [handleInitCheckout, handleCompleteCheckout, handleCancel])

  return (
    <>
      <div
        id="AmazonPayButton"
        ref={buttonContainerRef}
        style={{ width: '100%', minHeight: '48px' }}
      />

      {/* ===== 決済処理中モーダル ===== */}
      {isProcessing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '32px 40px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
            }}
          >
            {!processingError ? (
              <>
                {/* ローディングスピナー */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      border: '4px solid #e0e0e0',
                      borderTop: '4px solid #ff9900',
                      borderRadius: '50%',
                      animation: 'amazonPaySpin 1s linear infinite',
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '8px',
                  }}
                >
                  決済処理中です
                </p>
                <p
                  style={{
                    fontSize: '13px',
                    color: '#666',
                    lineHeight: '1.5',
                  }}
                >
                  このままお待ちください。
                  <br />
                  ページを閉じたり戻ったりしないでください。
                </p>
              </>
            ) : (
              <>
                {/* エラー表示 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '24px',
                        color: '#dc2626',
                      }}
                    >
                      ✕
                    </span>
                  </div>
                </div>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#dc2626',
                    lineHeight: '1.6',
                  }}
                >
                  {processingError}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== スピナーアニメーション ===== */}
      <style jsx global>{`
        @keyframes amazonPaySpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  )
}
